import { isOperationLoading, hasOperationLoaded } from '@gw/ui';
import { Navigate, Outlet, useSearchParams } from 'react-router-dom';

import { useAuthStore } from '@/stores/store';

/**
 * PublicRoute - Guards routes that should only be accessible to unauthenticated users
 * Redirects to authenticated routes if user is verified as authenticated.
 *
 * GW-443: Must wait for checkAuth to complete before trusting isAuthenticated
 * because isAuthenticated may be a stale persisted value from localStorage.
 *
 * GW-11401: Honor the `redirect` query param ProtectedRoute attaches when it
 * bounces an unauthenticated visitor to /auth/login. Without this, a visitor
 * who is (or becomes) authenticated by the time /auth/login mounts — e.g. the
 * stale-persisted-isAuthenticated race described above, or a transient token
 * refresh hiccup — gets sent to "/" instead of back to the deep link, and if
 * the auth flag flips again the two guards bounce the user between
 * /auth/login and "/" indefinitely (reported as a redirect loop while
 * accepting the Provider Agreement, since that flow's token-refresh call is
 * a common trigger). Only a same-origin relative path is honored, mirroring
 * the open-redirect guard in pendingMarketplaceRedirect.util.ts.
 */
export function PublicRoute() {
  const { isAuthenticated, isLoading, hasLoaded } = useAuthStore();
  const [searchParams] = useSearchParams();

  // Check if auth check is in progress
  const isCheckingAuth = isOperationLoading(isLoading, 'checkAuth');
  // GW-443: Check if auth check has completed (not just "not loading")
  const hasCheckedAuth = hasOperationLoaded(hasLoaded, 'checkAuth');

  // GW-443: Show loading state until checkAuth has COMPLETED verification
  // This prevents redirect based on stale persisted isAuthenticated value
  if (isCheckingAuth || !hasCheckedAuth) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Only redirect AFTER verification confirms user is actually authenticated
  if (isAuthenticated) {
    const redirect = searchParams.get('redirect');
    // Only allow relative paths to prevent open redirect
    const isSafeRedirect = !!redirect && redirect.startsWith('/') && !redirect.startsWith('//');
    return <Navigate to={isSafeRedirect ? redirect : '/'} replace />;
  }

  return <Outlet />;
}
